/* eslint-disable @typescript-eslint/no-explicit-any */
import { createStore, reconcile } from "solid-js/store";

import {
  GetUserPayChannelV2,
  GetUserPayUrlV2,
  PayPageOpenStatus,
  UserApplyPayV2,
} from "@/api/clientApi/wallet.ts";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { DepositKeyTypeEnum } from "@/enum/wallet.ts";
import { getLangKey, useI18n } from "@/i18n";
import { LocaleMap } from "@/i18n/config.ts";
import { sdkManager } from "@/lib/sdk/sdkManager.ts";
import { toOutLink } from "@/lib/utils.ts";
import { isValidNotEmpty } from "@/lib/validateUtils.ts";
import { getDefaultPointType, userStore } from "@/stores/userStore.ts";
import { getCryptoName } from "@/stores/wallet/withdrawStore.ts";
import { PayChannel } from "@/types/swagger/front.ts";
import {
  ChannelClassConfig,
  GetUserPayChannelRsp,
} from "@/types/swagger/userwealth.ts";

import { basicCoinSwap, cryptoStore } from "./cryptoStore.ts";
import { sysConfigStore } from "./sysConfigStore.ts";
import { FiatClassMap } from "./walletStore.ts";

const { showDialog } = useDialog();
const { t } = useI18n();

export type PayAttachField = {
  type: string;
  name: string;
  value: string;
  label: string;
  valid: boolean;
  identify_type?: number;
  lock?: boolean;
  rule?: (value: string) => boolean;
  error?: string | (() => string);
};

export type PayAttachValues = {
  [key: string]: string;
};

interface depositInterface {
  currentDepositModel: number;
  depositFiatStep: number;
  payModeList: {
    value: number;
    label: string;
  }[];
  fiat_channels: PayChannel[];
  channelDiscount: any;
  activityList: any;
  promoitemList: any;
  allActAndProList: any;
  extraId: string | number; // 2道具 1活动
  extraParam: string | number;
  selectedChannel: string; //选中提现渠道
  currentPointInfo: PayChannel;
  currentPayInfo: any;
  currentPointType: number | null;
  crypto_list: PayChannel[];
  pait_list: FiatClassMap;
  fiat_currency_config: any[];
  payAttachValues: PayAttachValues;
  phone_regex_cfg: string;
  formValid: boolean;
  orderData: any;
  reqCount: number;
  currentDepositInfo: any;
  qrCodeText: string;
  identify_type_cfg: any;
  loading: boolean;
  itemId: string;
}

export const [depositStore, setDepositStore] = createStore<depositInterface>({
  currentDepositModel: 0,
  depositFiatStep: 1,
  payModeList: [],
  fiat_channels: [] as PayChannel[],
  channelDiscount: {},
  activityList: [],
  promoitemList: [],
  allActAndProList: [],
  extraId: "",
  extraParam: -1,
  selectedChannel: "",
  currentPointInfo: {} as PayChannel,
  currentPayInfo: {} as PayChannel,
  currentPointType: null,
  crypto_list: [],
  pait_list: {} as FiatClassMap,
  fiat_currency_config: [],
  payAttachValues: {},
  phone_regex_cfg: "",
  formValid: false,
  orderData: {},
  reqCount: 10,
  currentDepositInfo: {},
  qrCodeText: "",
  /** 提现证件类型配置; key:证件类型 类型是map key是 int32 值是 IdentifyTypeCfg */
  identify_type_cfg: [],
  loading: false,
  itemId: "",
});

export const initDepositStore = async () => {
  setDepositStore(
    "fiat_currency_config",
    cryptoStore.fiat_currency_config.map((item) => {
      return {
        label: item.name,
        value: item.point_type,
      };
    }),
  );
  await getPayInfo();
  // await getDepositActivity();
  setDepositStore("payModeList", [{ value: 1, label: "法币/现金" }]);
  setDepositStore("currentDepositModel", 1);
};

// const getDepositActivity = () => {
//   GetUserPayDiscount().then((res) => {
//     console.log(res);
//     setDepositStore("channelDiscount", res);
//   });
// };

export const getPayInfo = async (pointType = sysConfigStore.point_type) => {
  setDepositStore("currentPointType", pointType);

  try {
    const res = await GetUserPayChannelV2({
      point_type: pointType,
    });

    if (res) {
      setPayClass(res);
      const selectedPayInfo =
        res.data.find((item) => item.default) || res.data[0];

      setDepositStore("channelDiscount", res.discount);
      // 数据结构使然 TODO: 格式不通用，逻辑需要重写
      if (((res.discount as any)?.activity_list?.length ?? 0) > 0) {
        setDepositStore("activityList", [
          // 自动模式/手动模式下，单个多个活动返回结构和取值不一样，手动模式下才有activity_list，TODO: 推动更改充值接口字段
          ...((res.discount as any)?.activity_list?.length > 0
            ? // 多个活动
              // eslint-disable-next-line no-unsafe-optional-chaining
              (res.discount as any)?.activity_list
                .map((item: any) => {
                  item.value = item?.atype;
                  let activityItem;
                  // 页面显示
                  if ([65].includes(item.atype)) {
                    item.label = `${t("bonus.daily_first_deposit")}`;
                    const vip_level = userStore.userInfo?.UserVip?.vip_level;
                    const vipItem = getVipItem(
                      item?.activity_config?.vip_cfg,
                      vip_level,
                    );
                    if (!vipItem) {
                      item.disabled = true;
                      return item;
                    } else {
                      item.disabled = false;
                    }
                    vipItem?.reward_cfg?.forEach((item) => {
                      item.max_reward_point = item.max_bonus;
                      item.point = item.min_pay_point;
                    });
                    activityItem =
                      vipItem?.reward_cfg?.[vipItem?.reward_cfg.length - 1];
                    item.activity_list = vipItem?.reward_cfg;
                    item.activity_config.list = activityItem;
                    item.description = t(
                      "bonus.daily_first_deposit_bonus_rules",
                      {
                        val1:
                          activityItem?.reward?.reward_point_mode === 1
                            ? `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                                activityItem?.reward?.point,
                                getDefaultPointType(),
                                true,
                                false,
                              )}</span> ${getCryptoName()}`
                            : `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${activityItem?.reward?.pay_pro / 100}%</span>`,
                      },
                    );
                  } else if ([13].includes(item.atype)) {
                    item.label = `${t("activity.first_deposit.title")}`;
                    activityItem = item?.activity_config?.list?.[0];
                    item.activity_list = item?.activity_config?.list;
                    item.description =
                      activityItem?.reward?.reward_point_mode === 1
                        ? t("deposit.consecutive_deposit_bonus", {
                            val1: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                              activityItem?.point,
                              getDefaultPointType(),
                              true,
                              false,
                            )}</span> ${getCryptoName()}`,
                            val2: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                              activityItem?.reward?.point,
                              getDefaultPointType(),
                              true,
                              false,
                            )}</span> ${getCryptoName()}`,
                          })
                        : t("deposit.consecutive_deposit_bonus", {
                            val1: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                              activityItem?.point,
                              getDefaultPointType(),
                              true,
                              false,
                            )}</span> ${getCryptoName()}`,
                            val2: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${activityItem?.reward?.pay_pro / 100}%</span>`,
                          });
                  } else {
                    item.label = `${t("common.bonus")}`;
                    activityItem = item?.activity_config?.list?.[0];
                    item.activity_list = item?.activity_config?.list;
                    item.description =
                      activityItem?.reward?.reward_point_mode === 1
                        ? t("deposit.consecutive_deposit_bonus", {
                            val1: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                              activityItem?.point,
                              getDefaultPointType(),
                              true,
                              false,
                            )}</span> ${getCryptoName()}`,
                            val2: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                              activityItem?.reward?.point,
                              getDefaultPointType(),
                              true,
                              false,
                            )}</span> ${getCryptoName()}`,
                          })
                        : t("deposit.consecutive_deposit_bonus", {
                            val1: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                              activityItem?.point,
                              getDefaultPointType(),
                              true,
                              false,
                            )}</span> ${getCryptoName()}`,
                            val2: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${activityItem?.reward?.pay_pro / 100}%</span>`,
                          });
                  }
                  return item;
                })
                .filter((item) => !item.disabled)
            : []),
          {
            label: t("deposit.without_bonus"),
            description: t("deposit.no_claim_bonus"),
            value: -1,
            disabled: false,
          },
        ]);
        setDepositStore("extraId", 1);
        setDepositStore("extraParam", depositStore?.activityList?.[0]?.value);
      }
      if (
        (res.discount as any)?.promoitem_list &&
        (res.discount as any)?.promoitem_list.length > 0
      ) {
        const promoitem_list = (res.discount as any)?.promoitem_list;
        const list = [
          // eslint-disable-next-line no-unsafe-optional-chaining
          ...promoitem_list?.map((item: any) => {
            let cfg_data, config, item_lang;
            if (item?.cfg_data) {
              cfg_data = JSON.parse(item.cfg_data);
              config = JSON.parse(cfg_data?.config);
              item_lang = JSON.parse(cfg_data?.item_lang);
            }
            const detail = config?.reward_list[0];
            return {
              ...item,
              cfg_data: cfg_data,
              config: config,
              item_lang: item_lang,
              value: item?.item_id,
              label: item_lang?.[getLangKey()]
                ? item_lang?.[getLangKey()]?.item_name
                : item_lang?.[LocaleMap.en]?.item_name,
              description:
                config?.reward_type === 0
                  ? t("deposit.consecutive_deposit_bonus", {
                      val1: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                        detail?.join_point,
                        getDefaultPointType(),
                        true,
                        false,
                      )}</span> ${getCryptoName()}`,
                      val2: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                        detail?.value,
                        getDefaultPointType(),
                        true,
                        false,
                      )}</span> ${getCryptoName()}`,
                    })
                  : t("deposit.consecutive_deposit_bonus", {
                      val1: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${basicCoinSwap(
                        detail?.join_point,
                        getDefaultPointType(),
                        true,
                        false,
                      )}</span> ${getCryptoName()}`,
                      val2: `<span class="text-(--colors-text-brand2) font-medium text-[14px]">${detail?.value / 10000}%</span>`,
                    }),
            };
          }),
          {
            label: t("deposit.without_bonus"),
            description: t("deposit.no_claim_bonus"),
            value: -1,
          },
        ];
        setDepositStore("promoitemList", list);
        // 没有活动才赋值
        if (((res.discount as any)?.activity_list?.length ?? 0) === 0) {
          setDepositStore("extraId", 2);
          setDepositStore(
            "extraParam",
            // res.discount?.promoitem_list?.[0]?.item_id,
            (res.discount as any)?.promoitem_list?.sort(
              (a: any, b: any) => a.end_time - b.end_time,
            )?.[0]?.item_id,
          );
        }
      }

      // 手动模式
      if (
        // (res.discount as any)?.mode === 1 &&
        depositStore.activityList.length > 0 ||
        depositStore.promoitemList.length > 0
      ) {
        const allList = Array.from(
          new Map(
            [
              ...depositStore.activityList.filter((item) => !item.disabled),
              ...depositStore.promoitemList,
              // 过滤掉-1
            ].map((item) => [item.value, item]),
          ).values(),
        ).sort((a, b) => (a.value === -1) - (b.value === -1));
        setDepositStore("allActAndProList", allList);
        if ((res.discount as any)?.mode === 1) {
          if (isValidNotEmpty(depositStore.itemId)) {
            setDepositStore("extraId", 2);
            setDepositStore("extraParam", depositStore.itemId);
          } else {
            // 多个活动+道具时的初始化
            if (allList?.[0]?.item_id) {
              setDepositStore("extraId", 2);
              setDepositStore("extraParam", allList?.[0]?.item_id);
            } else {
              setDepositStore("extraId", 1);
              setDepositStore("extraParam", allList[0]?.atype);
            }
          }
        }
      }
      setDepositStore("fiat_channels", res.data as PayChannel[]);
      setDepositStore("identify_type_cfg", res.identify_type_cfg);
      setDepositStore("selectedChannel", selectedPayInfo?.id as string);
      setDepositStore("currentPayInfo", selectedPayInfo as PayChannel);
      setDepositStore("phone_regex_cfg", res.phone_regex_cfg as any);
    }
  } catch (error) {
    console.error("getPayInfo error:", error);
    throw error;
  }
};

export const getVipItem = (arr, key) => {
  // 过滤出比 key 小或等于的 vip_level
  const filtered = arr.filter((i) => i.vip_level <= key);
  if (filtered.length === 0) return undefined; // 没有比 key 小的情况

  // 找出其中最大的 vip_level
  const maxLower = Math.max(...filtered.map((i) => i.vip_level));
  return filtered.find((i) => i.vip_level === maxLower);
};

// 设置法币和加密货币的列表
const setPayClass = (res: GetUserPayChannelRsp) => {
  let fiatClass: FiatClassMap = {};
  if (res.channel_class_cfg) {
    fiatClass = res.channel_class_cfg.reduce(
      (acc: FiatClassMap, item: ChannelClassConfig) => {
        acc[item.id] = {
          ...item,
          class: (item as any).class || "default",
          list: [],
          lang_text: item.lang_text[Object.keys(item.lang_text)[0]],
        };
        return acc;
      },
      {} as FiatClassMap,
    );
  }

  const cryptoClass: PayChannel[] = [];

  res.data.forEach((el: any) => {
    // 过滤最小金额
    if (el.pay_num_list) {
      el.pay_num_list = el.pay_num_list?.filter(
        (item: number) => item >= (el.pay_num_min ?? 0),
      );
    }
    // 拆分加密货币和法币
    if (el.pay_key == "crypto") {
      cryptoClass.push(el);
    } else {
      if (el.class_ids) {
        for (let i = 0; i < el.class_ids.length; i++) {
          if (fiatClass[el.class_ids[i]]) {
            fiatClass[el.class_ids[i]].list.push(el);
          }
        }
      } else {
        if (fiatClass["default"]) {
          fiatClass["default"].list.push(el);
        } else {
          fiatClass["default"] = {
            class: "存款方式",
            list: [el],
          };
        }
      }
    }
  });

  setDepositStore("crypto_list", reconcile(cryptoClass));
  setDepositStore("pait_list", reconcile(fiatClass));
};

// 更新支付校验信息
export const updatePayAttachValue = (fieldName: string, value: string) => {
  setDepositStore("payAttachValues", (prev) => ({
    ...prev,
    [fieldName]: value,
  }));
};
// 重置支付校验信息
export const resetPayAttachValues = (fields: PayAttachField[]) => {
  setDepositStore(
    "payAttachValues",
    reconcile(
      fields.reduce((acc, field) => {
        acc[field.name] = field.value;
        return acc;
      }, {} as PayAttachValues),
    ),
  );
};

// 计算奖金
export const getChannelReward = (
  point: number | string,
): { result: number; status: boolean; total: number } => {
  const channelRewardInfo = depositStore.currentPayInfo?.channel_reward;

  if (!depositStore.currentPayInfo.channel_reward_is_open) {
    return { result: Number(point), status: false, total: Number(point) };
  }
  if (Number(point) < channelRewardInfo.minimum_price) {
    return { result: Number(point), status: false, total: Number(point) };
  }
  if (channelRewardInfo.reward_point_mode === 1) {
    return {
      result: Math.min(channelRewardInfo.maximum_reward, Number(point)),
      status: true,
      total:
        Number(point) +
        Math.min(channelRewardInfo.maximum_reward, Number(point)),
    };
  } else {
    return {
      result: Math.min(channelRewardInfo.maximum_reward, Number(point)),
      status: true,
      total:
        Number(point) +
        Math.min(channelRewardInfo.maximum_reward, Number(point)),
    };
  }
};

const resolveDepositResponse = (res) => {
  setDepositStore("loading", false);
  if (res?.url.includes("http")) {
    if ((res as any)?.jump_type && (res as any)?.jump_type === 1) {
      toOutLink(res?.url);
    } else {
      showDialog({
        type: DialogType.DepositIframe,
        data: {
          iframeUrl: res?.url,
          countDownTime: res?.count_down_time,
        },
      });
    }
  } else {
    showDialog({
      type: DialogType.DepositPayment,
      data: {
        ...res,
        amount: depositStore.payAttachValues.amount,
        methodUrl: depositStore.currentPayInfo?.icon,
      },
    });
  }
};

const repeatDeposit = async (
  order_no: string,
  count: number = 0,
): Promise<any> => {
  const res = await GetUserPayUrlV2({ order_no });

  if (res?.url) {
    await PayPageOpenStatus({
      order_no: res?.order_no || "",
      status: 1,
      mac: "",
    });
    resolveDepositResponse(res);
    return res;
  }

  // 如果没拿到 url，继续轮询，最多重试 5 次
  if (count >= 5) {
    return res;
  }

  await new Promise((resolve) => setTimeout(resolve, 1000));
  return repeatDeposit(order_no, count + 1);
};

/**
 * 支付流程
 *
 * 1. 判断是否开启奖金
 * 2. 判断是否满足条件
 * 3. 满足条件则计算奖金
 * 4. 不满足条件则取支付列表第一个满足的
 * 5. 调用支付接口
 * 6. 调用支付接口成功后，获取支付链接
 * 7. 调用支付接口成功后，获取支付链接成功后，调用支付链接
 */
export const handleClickDeposit = async () => {
  setDepositStore("loading", true);
  // const res = await submitPay();
  // console.log("res11111");
  // console.log(res);
  // setDepositStore("currentDepositInfo", res);
  //
  // showDialog({
  //   type: DialogType.DepositPayment,
  //   data: { ...res, amount: depositStore.payAttachValues.amount },
  // });
  // isUsdt br暂时无用
  const isUsdt =
    depositStore.currentPayInfo?.pay_key === DepositKeyTypeEnum.usdt;
  const payload: any = {
    channel_id: depositStore.currentPayInfo.id,
    amount: depositStore.payAttachValues.amount,
    point_type: depositStore.currentPayInfo.point_type,

    pay_attach: {
      ...depositStore.payAttachValues,
      // identify: depositStore.payAttachValues.identify
      //   ? depositStore.payAttachValues.identify
      //       .replaceAll(".", "")
      //       .replaceAll("-", "")
      //   : "",
      // identify_type:
      //   depositStore.currentPayInfo.pay_attach?.identifys?.find(
      //     (item: any) => item.identify_type === 1,
      //   )?.identify_type || 0,
      identify_type: depositStore.payAttachValues?.identify_type,
      identify: depositStore.payAttachValues?.identify,
    },
    point: Number(depositStore.payAttachValues.amount) * 10000,
    // 手动使用
    ...(depositStore.channelDiscount.mode === 1 &&
      // 不使用
      depositStore.extraParam !== -1 &&
      depositStore.extraParam && {
        // 1.活动, 2.道具;
        extra_id: Number(depositStore.extraId) || 0,
        extra_param: `${depositStore.extraParam}` || "",
      }),
    // 自动使用
    ...(depositStore.channelDiscount.mode === 0 &&
      // 道具需要传递item_id
      depositStore.extraParam !== -1 &&
      Number(depositStore.extraId) === 2 &&
      depositStore.extraParam && {
        // 2.道具
        extra_id: Number(depositStore.extraId) || 0,
        extra_param: `${depositStore.extraParam}` || "",
      }),
  };
  // const kycRes = await KYCStore.getUserKycAuthByAction(
  //   UserKycAuthActionType.deposit,
  //   () =>
  //     UserApplyPayV2(payload, true).then(async (res) => {
  //       if (res?.user_auth) return res;
  //       setDepositStore("currentDepositInfo", res);

  //       if (!isUsdt) {
  //         if (res?.url) {
  //           PayPageOpenStatus({
  //             order_no: res?.order_no || "",
  //             status: 1,
  //             mac: "",
  //           });
  //           resolveDepositResponse(res);
  //           return res;
  //         } else {
  //           return await repeatDeposit(res?.order_no || "");
  //         }
  //       } else {
  //         setDepositStore("qrCodeText", res?.url || "");
  //         return res;
  //       }
  //       return res;
  //     }) as any,
  // );
  // if (!kycRes) {
  //   return;
  // }
  UserApplyPayV2(payload, true)
    .then(async (res) => {
      // if (res?.user_auth) return res;
      setDepositStore("currentDepositInfo", res);

      if (!isUsdt) {
        if (res?.url) {
          PayPageOpenStatus({
            order_no: res?.order_no || "",
            status: 1,
            mac: "",
          });
          resolveDepositResponse(res);
          return res;
        } else {
          return await repeatDeposit(res?.order_no || "");
        }
      } else {
        setDepositStore("loading", false);
        setDepositStore("qrCodeText", res?.url || "");
        return res;
      }
    })
    .catch((error) => {
      sdkManager.tdReportEvent("deposit_submit_error", {
        error: error.message,
      });
      setDepositStore("loading", false);
      console.error("handleClickDeposit error:", error);
      throw error;
    });
};
